import { Fragment, useLayoutEffect, useRef } from "react";
import { emitAscii, pick, randRange } from "../lib/asciiEmitter";

type Special = "launch" | "scale" | "splash";

type Token =
  | { type: "word"; text: string; italic?: boolean; special?: Special }
  | { type: "punct"; text: string }
  | { type: "space" }
  | { type: "br" };

const HEADING_TOKENS: Token[] = [
  { type: "word", text: "We" },
  { type: "space" },
  { type: "word", text: "provide" },
  { type: "space" },
  { type: "word", text: "unlimited", italic: true },
  { type: "space" },
  { type: "word", text: "AI‑native", italic: true },
  { type: "space" },
  { type: "word", text: "design", italic: true },
  { type: "br" },
  { type: "word", text: "that" },
  { type: "space" },
  { type: "word", text: "helps" },
  { type: "space" },
  { type: "word", text: "startups" },
  { type: "space" },
  { type: "word", text: "launch", special: "launch" },
  { type: "punct", text: "," },
  { type: "space" },
  { type: "word", text: "scale", special: "scale" },
  { type: "punct", text: "," },
  { type: "space" },
  { type: "word", text: "and" },
  { type: "br" },
  { type: "word", text: "make" },
  { type: "space" },
  { type: "word", text: "a" },
  { type: "space" },
  { type: "word", text: "splash", special: "splash" },
];

// Group tokens into rows, split at `br` markers. Each row is rendered inside
// a `.sub-row` clip container so animations that stray past the row's edges
// get clipped at the boundary. The `br` markers themselves don't render.
type RowEntry = { token: Token; index: number };
const ROWS: RowEntry[][] = (() => {
  const rows: RowEntry[][] = [[]];
  HEADING_TOKENS.forEach((token, index) => {
    if (token.type === "br") rows.push([]);
    else rows[rows.length - 1]!.push({ token, index });
  });
  return rows;
})();

// Indices of every animatable token (words + punctuation, including the
// three specials). All reveal in one batch via `.is-revealed` when the
// heading engages. The three special words' bespoke animations are no longer
// part of the entry sequence — they're hover-triggered instead.
const REVEAL_INDICES: number[] = HEADING_TOKENS.map((t, i) =>
  t.type === "punct" || t.type === "word" ? i : -1,
).filter((i) => i >= 0);

// --- easing helpers ---------------------------------------------------------
function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}
function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}
function easeInQuad(t: number): number {
  return t * t;
}
function easeOutBack(t: number, c: number = 1.4): number {
  const c3 = c + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
}
function easeOutElastic(t: number): number {
  if (t === 0 || t === 1) return t;
  const p = 0.32;
  return Math.pow(2, -10 * t) * Math.sin(((t - p / 4) * (2 * Math.PI)) / p) + 1;
}

type LaunchRefs = {
  outer: HTMLSpanElement | null;
  inner: HTMLSpanElement | null;
  particles: HTMLSpanElement | null;
};
type ScaleRefs = {
  outer: HTMLSpanElement | null;
  wrap: HTMLSpanElement | null;
  inner: HTMLSpanElement | null;
  frame: HTMLSpanElement | null;
  particles: HTMLSpanElement | null;
};
type SplashRefs = {
  outer: HTMLSpanElement | null;
  clip: HTMLSpanElement | null;
  inner: HTMLSpanElement | null;
  particles: HTMLSpanElement | null;
};

export function AnimatedSubHeadline() {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  // Refs to every fade-target token (normal words AND punct). Special words
  // have their own bespoke refs (launchRef / scaleRef / splashRef) and do
  // NOT live in this map.
  const tokenRefs = useRef<Map<number, HTMLSpanElement>>(new Map());
  const launchRef = useRef<LaunchRefs>({ outer: null, inner: null, particles: null });
  const scaleRef = useRef<ScaleRefs>({
    outer: null,
    wrap: null,
    inner: null,
    frame: null,
    particles: null,
  });
  const splashRef = useRef<SplashRefs>({
    outer: null,
    clip: null,
    inner: null,
    particles: null,
  });

  useLayoutEffect(() => {
    const heading = headingRef.current;
    if (!heading) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      // CSS @media handles the visual bypass; nothing to do here.
      return;
    }

    const played = new Set<number>();
    let intersected = false;
    // The .sub section's `.sub-headline-wrap` is invisible (opacity 0) until
    // `.sub.is-content-revealed` is added by CylinderCarousel.onEntryComplete.
    // If we trigger animations before that class arrives, the cascade plays
    // out behind a fully-transparent wrap and the user sees only the tail
    // when the wrap finally fades in. Gate on both signals.
    const sub = heading.closest(".sub") as HTMLElement | null;
    let sectionRevealed = sub ? sub.classList.contains("is-content-revealed") : true;
    let triggered = false;
    let pollRafId = 0;
    let rafId = 0;
    // Re-entry guards for the hover-triggered specials. Mouse-enters while
    // an animation is already running are ignored — re-firing mid-flight
    // would clobber the inline transforms the rAF tick is driving.
    let launchPlaying = false;
    let scalePlaying = false;
    let splashPlaying = false;
    const cleanups: Array<() => void> = [];

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            intersected = true;
            break;
          }
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px -10% 0px" },
    );
    io.observe(heading);

    let mo: MutationObserver | null = null;
    if (sub) {
      mo = new MutationObserver(() => {
        sectionRevealed = sub.classList.contains("is-content-revealed");
      });
      mo.observe(sub, { attributes: true, attributeFilter: ["class"] });
    }

    // Reveal a normal token (word or punct) by toggling `.is-revealed`.
    // Special words bypass this — they use their own bespoke timelines.
    const playPlainToken = (idx: number) => {
      const el = tokenRefs.current.get(idx);
      if (el) el.classList.add("is-revealed");
    };

    const playLaunch = () => {
      if (launchPlaying) return;
      const { outer, inner, particles } = launchRef.current;
      if (!outer || !inner || !particles) return;
      launchPlaying = true;

      // The rocket lives inside `.sub-row` which has overflow: hidden. Anything
      // we push past the row's top/bottom edges gets clipped — that's what
      // sells the "loop" trick below: the rocket exits the top in a squished
      // form, then a "second rocket" (same element, same squished form)
      // appears below the row and rises back to baseline.
      //
      // Initial pose: squished and anchored at baseline. scaleY 0.55 with
      // origin 50% 100% compresses the rocket toward the baseline — feet
      // planted, top compressed — while smoke wisps build up above.
      inner.style.transform = "translateY(0%) scaleY(0.55)";
      inner.style.transformOrigin = "50% 100%";
      inner.style.filter = "blur(2px)";
      outer.classList.add("is-armed");

      const rect = outer.getBoundingClientRect();
      const originX = rect.width / 2;
      const originY = rect.height;

      // Phase-1 warmup wisps: sparse, slow puffs that rise just above the
      // squished rocket. Visible (the row clip is well above), spool-up vibe.
      cleanups.push(
        emitAscii({
          container: particles,
          origin: { x: originX, y: originY * 0.62 },
          count: 9,
          spawnInterval: 65,
          charLadder: ["'", ".", ""],
          ladderInterval: 180,
          impulse: () => ({ vx: randRange(-18, 18), vy: randRange(-34, -14) }),
          gravity: -22,
          drag: 1.4,
          lifetime: () => randRange(520, 820),
          jitter: 1.8,
          jitterFreq: 1.2,
          fontSize: () => "14px",
        }),
      );

      // Timeline anchors (ms). Tweak these to retime the loop.
      const T_IGNITE = 500;
      const T_LIFTOFF_START = 560;
      const T_LIFTOFF_END = 1040;
      const T_LOOP_END = 1240; // off-screen pause: 200ms
      const T_LAND = 1620;
      const T_SETTLE = 1880;
      // Far-enough-off-row translate values so the row clip fully hides the
      // rocket during exit and pre-entry. Row line-height is ~2.1× font, the
      // rocket is ~1× font tall — these values comfortably clear the edges.
      const EXIT_Y = -240; // % (negative = up, above the row top)
      const ENTRY_Y = 240; // % (positive = down, below the row bottom)

      const start = performance.now();
      let exhaustFired = false;
      let trailFired = false;
      let dustFired = false;
      let stopped = false;
      cleanups.push(() => {
        stopped = true;
      });

      const tick = (now: number) => {
        if (stopped) return;
        const t = now - start;

        // Phase 1: Warmup (0 – T_IGNITE) — squished + tiny x-shake rumble.
        if (t < T_IGNITE) {
          const osc = Math.sin((t / 60) * Math.PI * 2);
          const jx = osc * 1.6;
          inner.style.transform = `translate(${jx}px, 0%) scaleY(0.55)`;
          inner.style.filter = "blur(2px)";
        }

        // Phase 2: Ignition burst at T_IGNITE — lateral mushroom of smoke.
        // Anything that flies below the baseline gets clipped by the row.
        if (t >= T_IGNITE && !exhaustFired) {
          exhaustFired = true;
          cleanups.push(
            emitAscii({
              container: particles,
              origin: { x: originX, y: originY },
              count: 18,
              charLadder: ["@", "#", "*", "o", "°", ".", ""],
              ladderInterval: 140,
              impulse: () => {
                const r = Math.random();
                let angle: number;
                if (r < 0.7) {
                  const side = Math.random() < 0.5 ? 1 : -1;
                  angle = side * randRange(Math.PI * 0.05, Math.PI * 0.45);
                } else if (r < 0.9) {
                  const side = Math.random() < 0.5 ? 1 : -1;
                  angle = side * randRange(Math.PI * 0.55, Math.PI * 0.85);
                } else {
                  angle = randRange(Math.PI * 0.4, Math.PI * 0.6) + Math.PI;
                }
                const speed = randRange(90, 170);
                return { vx: Math.cos(angle) * speed, vy: -Math.sin(angle) * speed };
              },
              gravity: -55,
              drag: 1.7,
              lifetime: () => randRange(700, 1100),
              jitter: 2,
              jitterFreq: 1.4,
              fontSize: () => pick(["14px", "18px", "22px"]),
              rotate: 30,
            }),
          );
        }

        // Phase 3: Liftoff (T_LIFTOFF_START – T_LIFTOFF_END) — rocket rises
        // and exits the top of the row. Stays squished — the exit form has
        // to match the re-entry form for the loop illusion to read.
        if (t >= T_LIFTOFF_START && t < T_LIFTOFF_END) {
          const local = (t - T_LIFTOFF_START) / (T_LIFTOFF_END - T_LIFTOFF_START);
          const eased = Math.pow(local, 2.6); // steep ease-in (acceleration)
          const y = EXIT_Y * eased;
          const blur = 2 * (1 - eased);
          inner.style.transform = `translate(0px, ${y}%) scaleY(0.55)`;
          inner.style.filter = `blur(${blur}px)`;
        }

        // Trail smoke kicks in shortly after liftoff begins.
        if (t >= T_LIFTOFF_START + 60 && !trailFired) {
          trailFired = true;
          cleanups.push(
            emitAscii({
              container: particles,
              origin: { x: originX, y: originY * 0.9 },
              count: 8,
              spawnInterval: 55,
              charLadder: ["o", "°", ".", ""],
              ladderInterval: 90,
              impulse: () => {
                const side = Math.random() < 0.5 ? -1 : 1;
                return {
                  vx: side * randRange(15, 40),
                  vy: randRange(20, 60),
                };
              },
              gravity: 20,
              drag: 2.0,
              lifetime: () => randRange(380, 600),
              fontSize: () => pick(["12px", "15px"]),
              rotate: 15,
            }),
          );
        }

        // Phase 4: Off-screen "loop" gap (T_LIFTOFF_END – T_LOOP_END). The
        // rocket holds far above the row — fully clipped, invisible. The
        // viewer's eye fills in the loop they didn't see.
        if (t >= T_LIFTOFF_END && t < T_LOOP_END) {
          inner.style.transform = `translate(0px, ${EXIT_Y}%) scaleY(0.55)`;
          inner.style.filter = "blur(0)";
        }

        // Phase 5: Re-entry from below (T_LOOP_END – T_LAND). Same squished
        // shape as the exit. Eases out so the landing reads as a deliberate
        // touchdown, not a slam.
        if (t >= T_LOOP_END && t < T_LAND) {
          const local = (t - T_LOOP_END) / (T_LAND - T_LOOP_END);
          const eased = easeOutQuart(local);
          const y = ENTRY_Y + (0 - ENTRY_Y) * eased;
          inner.style.transform = `translate(0px, ${y}%) scaleY(0.55)`;
          inner.style.filter = "blur(0)";
        }

        // Dust kick just before landing.
        if (t >= T_LAND - 70 && !dustFired) {
          dustFired = true;
          cleanups.push(
            emitAscii({
              container: particles,
              origin: { x: originX, y: originY },
              count: 6,
              charLadder: ["*", ".", ""],
              ladderInterval: 90,
              impulse: () => {
                const side = Math.random() < 0.5 ? -1 : 1;
                return {
                  vx: side * randRange(50, 95),
                  vy: -randRange(15, 40),
                };
              },
              gravity: 60,
              drag: 2.6,
              lifetime: () => randRange(280, 480),
              fontSize: () => "13px",
            }),
          );
        }

        // Phase 6: Settle (T_LAND – T_SETTLE) — springs from squished (0.55)
        // to full height (1.0) with a small back-overshoot.
        if (t >= T_LAND && t < T_SETTLE) {
          const local = (t - T_LAND) / (T_SETTLE - T_LAND);
          const eased = easeOutBack(local, 1.6);
          const scale = 0.55 + (1.0 - 0.55) * eased;
          inner.style.transform = `translate(0px, 0%) scaleY(${scale})`;
          inner.style.filter = "blur(0)";
        }

        if (t >= T_SETTLE) {
          inner.style.transform = "none";
          inner.style.filter = "none";
          launchPlaying = false;
          return;
        }

        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);
    };

    const playScale = () => {
      if (scalePlaying) return;
      const { outer, wrap, frame, particles } = scaleRef.current;
      if (!outer || !wrap || !frame || !particles) return;
      scalePlaying = true;

      // Single wrapper holds both the word and the frame. Scaling the wrapper
      // grows them in lockstep from a tiny bottom-left-anchored point up to
      // their natural size — the frame's `inset: -5px -7px` keeps the visual
      // padding around the word consistent at every scale step.
      wrap.style.transform = "scale(0)";
      wrap.style.transformOrigin = "0% 100%";
      frame.style.opacity = "1";
      outer.classList.add("is-armed");

      const rect = outer.getBoundingClientRect();
      const cornerX = rect.width; // top-right corner of the final word
      const cornerY = 0;

      const start = performance.now();
      let sparkFired = false;
      let stopped = false;
      cleanups.push(() => {
        stopped = true;
      });

      const tick = (now: number) => {
        if (stopped) return;
        const t = now - start;

        // Phase 1: Diagonal grow (0–600ms) — wrap scales 0 → 1 from
        // bottom-left. Word and frame both scale with it.
        if (t < 600) {
          const local = t / 600;
          const eased = easeOutQuart(local);
          wrap.style.transform = `scale(${eased})`;
        }

        // Phase 2: Impact tick (600–630ms) — tiny full-frame jitter as it
        // "snaps" into final size.
        if (t >= 600 && t < 630) {
          const jx = (Math.random() - 0.5) * 1.6;
          const jy = (Math.random() - 0.5) * 1.6;
          wrap.style.transform = `translate(${jx}px, ${jy}px) scale(1)`;
        }

        // Phase 3: Spark burst at impact, aimed up-and-right.
        if (t >= 600 && !sparkFired) {
          sparkFired = true;
          cleanups.push(
            emitAscii({
              container: particles,
              origin: { x: cornerX, y: cornerY },
              count: 7,
              charLadder: ["*", "+", "·", ""],
              ladderInterval: 90,
              impulse: () => {
                const angle = randRange(Math.PI * 0.05, Math.PI * 0.42);
                const speed = randRange(130, 200);
                return { vx: Math.cos(angle) * speed, vy: -Math.sin(angle) * speed };
              },
              gravity: 280,
              drag: 2.5,
              lifetime: () => randRange(260, 420),
              fontSize: () => pick(["12px", "16px"]),
            }),
          );
        }

        // Phase 4: Frame dissolves (630–830ms), word stays at full size.
        if (t >= 630 && t < 830) {
          const local = (t - 630) / 200;
          wrap.style.transform = "scale(1)";
          frame.style.opacity = String(1 - local);
        }

        if (t >= 830) {
          // Reset to natural state so subsequent hovers start clean.
          wrap.style.transform = "";
          frame.style.opacity = "";
          scalePlaying = false;
          return;
        }

        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);
    };

    const playSplash = () => {
      if (splashPlaying) return;
      const { outer, inner, particles } = splashRef.current;
      if (!outer || !inner || !particles) return;
      splashPlaying = true;

      inner.style.transform = "translateY(-100%)";
      inner.style.opacity = "1";
      outer.classList.add("is-armed");

      const rect = outer.getBoundingClientRect();
      const originX = rect.width / 2;
      const originY = rect.height * 0.92;

      const start = performance.now();
      let dropletsFired = false;
      let stopped = false;
      cleanups.push(() => {
        stopped = true;
      });

      const tick = (now: number) => {
        if (stopped) return;
        const t = now - start;

        // Phase 1: Drop (0–460ms): translateY -100% → 0 with easeIn
        if (t < 460) {
          const local = t / 460;
          const eased = easeInQuad(local);
          const y = -100 + 100 * eased;
          inner.style.transform = `translateY(${y}%)`;
        }

        // Phase 2: Impact squash (460–540ms)
        if (t >= 460 && t < 540) {
          const local = (t - 460) / 80;
          const eased = easeOutQuad(local);
          const sy = 1 + (0.62 - 1) * eased;
          const sx = 1 + (1.18 - 1) * eased;
          inner.style.transform = `translateY(0) scale(${sx}, ${sy})`;
          inner.style.transformOrigin = "50% 100%";
        }

        // Droplets at impact — like stepping in a puddle, drops only spray
        // UP and outward. Shorter lifetime + stronger initial upward velocity
        // mean each drop fades near or before its descent peak; the row clip
        // hides any rare overshoot below baseline so nothing visually
        // "kicks through the floor".
        if (t >= 460 && !dropletsFired) {
          dropletsFired = true;
          cleanups.push(
            emitAscii({
              container: particles,
              origin: { x: originX, y: originY },
              count: 11,
              charLadder: ['"', "'", "°", "*", ".", ""],
              ladderInterval: 90,
              impulse: () => {
                // Higher launch angles + faster speed → drops climb more,
                // hang briefly at apex.
                const side = Math.random() < 0.5 ? -1 : 1;
                const angle = randRange(Math.PI * 0.32, Math.PI * 0.5);
                const speed = randRange(120, 200);
                return {
                  vx: side * Math.cos(angle) * speed,
                  vy: -Math.sin(angle) * speed,
                };
              },
              gravity: 380,
              drag: 2.0,
              lifetime: () => randRange(280, 420),
              fontSize: () => pick(["13px", "16px", "19px"]),
              rotate: 25,
            }),
          );
        }

        // Phase 3: Spring back (540–900ms): elastic to natural
        if (t >= 540 && t < 900) {
          const local = (t - 540) / 360;
          const eased = easeOutElastic(local);
          const sy = 0.62 + (1 - 0.62) * eased;
          const sx = 1.18 + (1 - 1.18) * eased;
          inner.style.transform = `translateY(0) scale(${sx}, ${sy})`;
        }

        if (t >= 900) {
          inner.style.transform = "none";
          splashPlaying = false;
          return;
        }

        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);
    };

    const playToken = (idx: number) => {
      const token = HEADING_TOKENS[idx];
      if (!token) return;
      if (token.type === "punct") {
        playPlainToken(idx);
        return;
      }
      if (token.type !== "word") return;
      if (token.special === "launch") playLaunch();
      else if (token.special === "scale") playScale();
      else if (token.special === "splash") playSplash();
      else playPlainToken(idx);
    };

    const engageAutoPlay = () => {
      // Single batch: every word + comma fades in together. The three special
      // words show at natural size from this point on; their bespoke
      // animations only fire on hover.
      for (const idx of REVEAL_INDICES) {
        if (played.has(idx)) continue;
        played.add(idx);
        playPlainToken(idx);
      }
    };

    // Wire up hover triggers for the three special words — but only on
    // viewports wider than 1024px. At or below that width we're either on a
    // touch device (no real hover) or in a compact desktop where the row
    // clip frames are too tight for the rocket loop / scale grow / splash
    // physics to read cleanly. Specials still reveal as normal text; the
    // bespoke animations are simply dormant.
    const isHoverViewport = window.matchMedia("(min-width: 1025px)").matches;
    if (isHoverViewport) {
      const attachHover = (
        outer: HTMLSpanElement | null,
        play: () => void,
        isPlayingGet: () => boolean,
      ) => {
        if (!outer) return;
        const handler = () => {
          if (isPlayingGet()) return;
          play();
        };
        outer.addEventListener("mouseenter", handler);
        cleanups.push(() => outer.removeEventListener("mouseenter", handler));
      };
      attachHover(launchRef.current.outer, playLaunch, () => launchPlaying);
      attachHover(scaleRef.current.outer, playScale, () => scalePlaying);
      attachHover(splashRef.current.outer, playSplash, () => splashPlaying);
    }

    const tryEngage = () => {
      if (triggered) return;
      if (!intersected || !sectionRevealed) return;
      const rect = heading.getBoundingClientRect();
      // Wait until the heading is meaningfully in view (top in the upper
      // two-thirds of the viewport) before kicking off the cascade.
      if (rect.top > window.innerHeight * 0.65) return;
      triggered = true;
      engageAutoPlay();
    };

    const pollEngagement = () => {
      tryEngage();
      if (!triggered) pollRafId = requestAnimationFrame(pollEngagement);
    };
    pollRafId = requestAnimationFrame(pollEngagement);

    return () => {
      cancelAnimationFrame(pollRafId);
      cancelAnimationFrame(rafId);
      io.disconnect();
      mo?.disconnect();
      for (const fn of cleanups) {
        try {
          fn();
        } catch {
          // ignore
        }
      }
    };
  }, []);

  const renderToken = (token: Token, i: number) => {
    // `br` is a row-split marker only — rendered as a new `.sub-row` by the
    // outer map, never emitted into the DOM directly.
    if (token.type === "br") return null;
    if (token.type === "space") return <Fragment key={i}> </Fragment>;
    if (token.type === "punct") {
      return (
        <span
          key={i}
          className="ap"
          data-w-idx={i}
          ref={(el) => {
            if (el) tokenRefs.current.set(i, el);
            else tokenRefs.current.delete(i);
          }}
        >
          {token.text}
        </span>
      );
    }

    const inner = renderWord(token, i);
    if (token.italic) return <em key={i}>{inner}</em>;
    return <Fragment key={i}>{inner}</Fragment>;
  };

  const renderWord = (token: Extract<Token, { type: "word" }>, i: number) => {
    if (token.special === "launch") {
      return (
        <span
          className="aw aw--launch"
          data-w-idx={i}
          ref={(el) => {
            launchRef.current.outer = el;
            if (el) tokenRefs.current.set(i, el);
            else tokenRefs.current.delete(i);
          }}
        >
          <span
            className="aw-particles"
            aria-hidden="true"
            ref={(el) => {
              launchRef.current.particles = el;
            }}
          />
          <span
            className="aw-launch-inner"
            ref={(el) => {
              launchRef.current.inner = el;
            }}
          >
            {token.text}
          </span>
        </span>
      );
    }

    if (token.special === "scale") {
      return (
        <span
          className="aw aw--scale"
          data-w-idx={i}
          ref={(el) => {
            scaleRef.current.outer = el;
            if (el) tokenRefs.current.set(i, el);
            else tokenRefs.current.delete(i);
          }}
        >
          <span
            className="aw-particles"
            aria-hidden="true"
            ref={(el) => {
              scaleRef.current.particles = el;
            }}
          />
          <span
            className="aw-scale-wrap"
            ref={(el) => {
              scaleRef.current.wrap = el;
            }}
          >
            <span
              className="aw-scale-inner"
              ref={(el) => {
                scaleRef.current.inner = el;
              }}
            >
              {token.text}
            </span>
            <span
              className="aw-scale-frame"
              aria-hidden="true"
              ref={(el) => {
                scaleRef.current.frame = el;
              }}
            >
              <span className="aw-scale-handle aw-scale-handle--tl" />
              <span className="aw-scale-handle aw-scale-handle--tr" />
              <span className="aw-scale-handle aw-scale-handle--bl" />
              <span className="aw-scale-handle aw-scale-handle--br" />
            </span>
          </span>
        </span>
      );
    }

    if (token.special === "splash") {
      return (
        <span
          className="aw aw--splash"
          data-w-idx={i}
          ref={(el) => {
            splashRef.current.outer = el;
            if (el) tokenRefs.current.set(i, el);
            else tokenRefs.current.delete(i);
          }}
        >
          <span
            className="aw-particles"
            aria-hidden="true"
            ref={(el) => {
              splashRef.current.particles = el;
            }}
          />
          <span
            className="aw-splash-clip"
            ref={(el) => {
              splashRef.current.clip = el;
            }}
          >
            <span
              className="aw-splash-inner"
              ref={(el) => {
                splashRef.current.inner = el;
              }}
            >
              {token.text}
            </span>
          </span>
        </span>
      );
    }

    return (
      <span
        className="aw"
        data-w-idx={i}
        ref={(el) => {
          if (el) tokenRefs.current.set(i, el);
          else tokenRefs.current.delete(i);
        }}
      >
        {token.text}
      </span>
    );
  };

  return (
    <h2 className="sub-headline" ref={headingRef}>
      {ROWS.map((row, rowIdx) => (
        <span className="sub-row" key={rowIdx}>
          {row.map(({ token, index }) => renderToken(token, index))}
        </span>
      ))}
    </h2>
  );
}
